<template>
  <my-giving-layout>
    <div class="bg-[#F6F4F0] w-full min-h-screen">
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
      <div class="px-[20px] pb-[64px]">
        <div class="main-title">Automate your giving</div>

        <div class="content-section">
          <p class="description">
            Recurring giving is a practical way to carry the church on our hearts all year round.
            More than convenience, it's about consistency. By automating the important, we ensure we
            stay faithful with our tithes and offerings, even when life gets busy 🙌🏻
          </p>

          <!-- Verse Card -->
          <div class="verse-card">
            <div class="card-content">
              <p class="verse-text">
                "Honor the LORD with your wealth, with the firstfruits of all your crops; then your
                barns will be filled to overflowing, and your vats will brim over with new wine."
                <br /><br />
                <span class="verse-reference">Proverbs 3:9–10 (NIV)</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Bottom Button -->
        <div class="bottom-section">
          <button class="get-started-button" @click="getStarted">Let's Get Started!</button>
        </div>
      </div>
    </div>

    <!-- No linked account modal -->
    <Modal v-model="showModal" :show-close="false" container-class="modal-custom-spacing">
      <template #header>
        <div
          class="text-left text-[1.5rem] leading-[2rem] font-[900] text-[#2B2926] tracking-[-0.72px]"
        >
          Oops, you don't have a linked account yet!
        </div>
      </template>

      <div class="text-left text-[1rem] leading-[1.5rem] font-medium text-[#2B2926]">
        To set up recurring giving, add a bank account to HOGC ONE first!
      </div>

      <template #footer>
        <div class="flex gap-[16px] w-full">
          <button
            class="flex-1 py-[12px] px-[24px] rounded-[100px] bg-transparent border-none text-[1rem] leading-[1.5rem] font-bold text-[#21201E] tracking-[-0.48px]"
            @click="maybeLater"
          >
            Maybe Later
          </button>
          <button
            class="flex-1 py-[12px] px-[24px] rounded-[100px] bg-transparent border-solid border-[2px] border-[#42362B] text-[1rem] leading-[1.5rem] font-bold text-[#21201E] tracking-[-0.48px]"
            @click="addNow"
          >
            Add Now
          </button>
        </div>
      </template>
    </Modal>
  </my-giving-layout>
</template>

<script>
import BackButton from '@/components/common/BackButton.vue';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import Modal from '@/components/common/Modal.vue';

export default {
  name: 'SetupRecurringStart',
  components: {
    BackButton,
    MyGivingLayout,
    Modal,
  },
  data() {
    return {
      showModal: false,
    };
  },
  methods: {
    back() {
      this.$router.go(-1);
    },

    getStarted() {
      const linkedAccounts = this.$store.getters['myGiving/linkedBankAccounts'];
      if (linkedAccounts && linkedAccounts.length > 0) {
        this.$router.push({ path: '/my-giving/giving-options', query: { fromrecurring: true } });
      } else {
        this.showModal = true;
      }
    },

    maybeLater() {
      // Close modal and go back
      this.showModal = false;
    },

    addNow() {
      // Close modal and navigate to list of banks
      this.showModal = false;
      this.$router.push({ path: '/my-giving/list-of-banks', query: { fromrecurring: true } });
    },
  },
};
</script>

<style scoped>
.main-title {
  font-family: 'Inter', sans-serif;
  font-weight: 900;
  font-size: 32px;
  line-height: 36px;
  color: #191817;
  letter-spacing: -0.96px;
  margin: 0 0 40px 0;
  width: 100%;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  margin-bottom: 40px;
}

.description {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #21201e;
  margin: 0;
  width: 100%;
}

/* Verse Card */
.verse-card {
  width: 100%;
}

.card-content {
  background-color: white !important;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  padding: 12px 16px 16px 16px;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.verse-text {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #1d1d1d;
  text-align: center;
  letter-spacing: -0.084px;
  margin: 0;
}

.verse-reference {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
}

/* Bottom Section */
.bottom-section {
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
}

.get-started-button {
  background-color: #42362b;
  color: white;
  border: none;
  border-radius: 100px;
  padding: 16px 24px;
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  box-sizing: border-box;
  white-space: nowrap;
}

.get-started-button:hover {
  background-color: #3a2f25;
}

.get-started-button:active {
  background-color: #322a20;
}

/* Modal custom spacing to match Figma design */
:deep(.modal-custom-spacing) {
  padding: 24px !important;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

:deep(.modal-custom-spacing > div:nth-child(1)) {
  margin-bottom: 0 !important;
  text-align: left !important;
}

:deep(.modal-custom-spacing > div:nth-child(1) h3) {
  font-size: 24px !important;
  line-height: 32px !important;
  font-weight: 900 !important;
  color: #2b2926 !important;
  letter-spacing: -0.72px !important;
  text-align: left !important;
  margin: 0 !important;
}

:deep(.modal-custom-spacing > div:nth-child(2)) {
  margin-top: 0 !important;
  min-height: auto !important;
  max-height: none !important;
  text-align: left !important;
}

:deep(.modal-custom-spacing > div:nth-child(3)) {
  margin-top: 0 !important;
  justify-content: center !important;
}
</style>
